import type { GetImpulseFormOutput, GetImpulseFormOutputVerbose } from "~/tools/impulse-form";

import type { BarChart } from "../../../../common/dashboard-spec/chart-schemas";
import { BarChartFormState } from "../../../widget-form/bar-chart-form-state";
import type { WidgetFormSync } from "../../../widget-form/sync";
import type { FormProcessor } from "../../_form-processor";
import { FormState as LegendFormState } from "../_legend/form-state";
import { StepFormState as ThresholdStepFormState } from "../_thresholds/step-form-state";

type Spec = BarChart["spec"];

const DEFAULT = BarChartFormState.default;

function specToInput({ yAxis, legend, thresholds }: Spec) {
	return {
		unit: yAxis?.format?.unit ?? DEFAULT.yAxis.element.unit,
		decimals: yAxis?.format?.decimalPlaces?.toString() ?? DEFAULT.yAxis.element.decimals,

		min: yAxis?.min?.toString() ?? DEFAULT.range.min,
		max: yAxis?.max?.toString() ?? DEFAULT.range.max,

		legend: LegendFormState.specToInput(legend),

		yAxis: {
			enabled: yAxis?.show ?? DEFAULT.yAxis.enabled,
			label: yAxis?.label ?? DEFAULT.yAxis.element.label,
		},

		thresholds: {
			mode: thresholds?.mode ?? DEFAULT.thresholds.mode,
			steps: thresholds?.steps.map(ThresholdStepFormState.specToInput) ?? DEFAULT.thresholds.steps,
		},
	} satisfies WidgetFormSync;
}

type FormState = BarChartFormState;

function outputToSpec({ range, legend, yAxis, thresholds }: GetImpulseFormOutput<FormState>): Spec {
	return {
		legend: LegendFormState.outputToSpec(legend),

		yAxis: {
			show: yAxis != null,
			format: {
				unit: yAxis?.unit,
				decimalPlaces: yAxis?.decimals,
			},
			label: yAxis?.label,
			min: range.min,
			max: range.max,
		},

		thresholds: {
			mode: thresholds.mode,
			steps: thresholds.steps.map(ThresholdStepFormState.outputToSpec),
		},
	};
}

function verboseOutputToSpec({ range, legend, yAxis, thresholds }: GetImpulseFormOutputVerbose<FormState>): Spec {
	return {
		legend: LegendFormState.verboseOutputToSpec(legend),

		yAxis: {
			show: yAxis.enabled ?? undefined,
			format: {
				unit: yAxis.element.unit ?? undefined,
				decimalPlaces: yAxis.element.decimals ?? undefined,
			},
			label: yAxis.element.label ?? undefined,
			min: range.min ?? undefined,
			max: range.max ?? undefined,
		},

		thresholds: {
			mode: thresholds.mode ?? undefined,
			steps: thresholds.steps.map(ThresholdStepFormState.verboseOutputToSpec),
		},
	};
}

type Input = ReturnType<typeof specToInput>;

function init(sync?: Input): FormState {
	const initial = sync == null ? undefined : BarChartFormState.fromSync(sync);

	return BarChartFormState.init(initial);
}

function setInput(input: Input, state: FormState): void {
	BarChartFormState.setInput(BarChartFormState.fromSync(input), state);
}

function resetToDefault(state: FormState): void {
	BarChartFormState.setInput(BarChartFormState.default, state);
}

const FormState = {
	specToInput,
	outputToSpec,
	verboseOutputToSpec,
	init,
	setInput,
	resetToDefault,
} satisfies FormProcessor<Spec, FormState, Input>;

export { FormState };
